import React, { useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FaEye, FaTimes, FaTrash } from "react-icons/fa";
import {
  RECEIPT_FILE_ACCEPT,
  buildReceiptPreviewUrl,
  isAllowedReceiptFile,
  isReceiptImageFile,
  isReceiptPdfFile,
} from "../../utilis/financeInvoicePaymentHelpers";
import {
  buildFinancePaymentLineRows,
  splitPaymentTotalAcrossLineRows,
} from "../../utilis/financeInvoicePaymentLineHelpers";
import {
  formatInvoiceMoney,
  INVOICE_AMOUNT_MAX_DECIMALS,
  normalizeInvoiceAmountInput,
  sanitizeDecimalInput,
} from "../../utilis/invoiceLineItemHelpers";

const PAYMENT_INPUT_DECIMALS = INVOICE_AMOUNT_MAX_DECIMALS;

const PAYMENT_METHOD_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "card", label: "Card" },
  { value: "cheque", label: "Cheque" },
  { value: "other", label: "Other" },
];

const EMPTY_LINE_ITEMS = [];
const EMPTY_DRAFT_PAYMENTS = [];
const EMPTY_TRANSACTIONS = [];

function toDateInputValue(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function createEmptyPaymentForm() {
  return {
    amount: "",
    transaction_date: toDateInputValue(),
    payment_method: "cash",
    reference: "",
    description: "",
  };
}

function createLineAmountState(rows = []) {
  return rows.reduce((state, row) => {
    state[row.line_number] = "";
    return state;
  }, {});
}

function FinanceInvoicePaymentModal({
  show = false,
  onClose,
  onSave,
  onSaveBulk = null,
  saving = false,
  loading = false,
  loadError = "",
  loadingWideLayout = false,
  currency = "AUD",
  amountDue = 0,
  invoiceNumber = "",
  lineItems = EMPTY_LINE_ITEMS,
  draftPayments = EMPTY_DRAFT_PAYMENTS,
  existingTransactions = EMPTY_TRANSACTIONS,
  /** When true, keep multi-line layout for the whole session (bulk save). */
  forceLineAllocation = false,
}) {
  const receiptInputId = useId();
  const [form, setForm] = useState(createEmptyPaymentForm);
  const [lineAmounts, setLineAmounts] = useState({});
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [receiptPreviews, setReceiptPreviews] = useState([]);
  const [error, setError] = useState("");
  /** Multi-line: user enters one total; optional split across line inputs. */
  const [allocationTotalInput, setAllocationTotalInput] = useState("");
  /** Snapshot when modal opens — avoids layout/state flicker while bulk payments save. */
  const [paymentSession, setPaymentSession] = useState(null);

  const dueAmount = Math.max(Number(amountDue) || 0, 0);

  const payableLineRows = useMemo(
    () =>
      buildFinancePaymentLineRows({
        lineItems,
        draftPayments,
        existingTransactions,
      }),
    [lineItems, draftPayments, existingTransactions],
  );

  const displayLineRows = paymentSession?.rows ?? payableLineRows;
  /** Layout is chosen once when the modal opens — never switches mid-session. */
  const useLineAllocation = paymentSession
    ? paymentSession.useLineAllocation
    : forceLineAllocation ||
      loadingWideLayout ||
      (!loading && payableLineRows.length > 1);
  const displayDueAmount = paymentSession?.amountDue ?? dueAmount;
  const useWideLayout = useLineAllocation || loadingWideLayout;
  const isBusy = saving || loading;
  const showForm = !loading && !loadError;

  const allocatedTotal = useMemo(() => {
    if (!useLineAllocation) return 0;
    return displayLineRows.reduce((sum, row) => {
      return sum + Math.max(Number(lineAmounts[row.line_number]) || 0, 0);
    }, 0);
  }, [useLineAllocation, displayLineRows, lineAmounts]);

  const paymentAmount = useLineAllocation
    ? allocatedTotal
    : Number(form.amount) || 0;

  useEffect(() => {
    if (!show) return undefined;

    setForm(createEmptyPaymentForm());
    setLineAmounts({});
    setAllocationTotalInput("");
    setReceiptFiles([]);
    setReceiptPreviews([]);
    setError("");
    setPaymentSession(null);

    return () => {
      receiptPreviews.forEach((preview) => {
        if (preview) URL.revokeObjectURL(preview);
      });
    };
  }, [show]);

  useEffect(() => {
    if (!show || loading) return;

    setPaymentSession((prev) => {
      if (prev?.locked) {
        return { ...prev, amountDue: dueAmount };
      }

      const rows = payableLineRows;
      const multiLine = forceLineAllocation || rows.length > 1;

      return {
        locked: true,
        rows,
        useLineAllocation: multiLine,
        amountDue: dueAmount,
      };
    });
  }, [show, loading, forceLineAllocation, payableLineRows, dueAmount]);

  useEffect(() => {
    if (!show || !paymentSession?.locked) return;

    setLineAmounts((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      return createLineAmountState(paymentSession.rows);
    });
  }, [show, paymentSession]);

  const canSave = useMemo(() => {
    return (
      showForm &&
      !saving &&
      displayDueAmount > 0 &&
      paymentAmount > 0 &&
      paymentAmount <= displayDueAmount + 0.0001 &&
      String(form.transaction_date || "").trim() &&
      String(form.payment_method || "").trim()
    );
  }, [showForm, saving, displayDueAmount, paymentAmount, form]);

  const handleFieldChange = (field, value) => {
    const nextValue =
      field === "amount"
        ? sanitizeDecimalInput(value, PAYMENT_INPUT_DECIMALS)
        : value;
    setForm((prev) => ({ ...prev, [field]: nextValue }));
    setError("");
  };

  const handleAmountBlur = () => {
    setForm((prev) => ({
      ...prev,
      amount: normalizeInvoiceAmountInput(prev.amount),
    }));
  };

  const syncAllocationTotalInputFromLines = (amounts) => {
    const sum = displayLineRows.reduce((total, row) => {
      return total + Math.max(Number(amounts[row.line_number]) || 0, 0);
    }, 0);
    setAllocationTotalInput(
      sum > 0 ? normalizeInvoiceAmountInput(String(sum)) : "",
    );
  };

  const applyLiveAllocationSplit = (sanitizedTotal) => {
    if (!sanitizedTotal || sanitizedTotal === ".") {
      setLineAmounts(createLineAmountState(displayLineRows));
      setError("");
      return;
    }

    const total = Number(sanitizedTotal);
    if (!Number.isFinite(total) || total <= 0) {
      setLineAmounts(createLineAmountState(displayLineRows));
      setError("");
      return;
    }

    if (total > displayDueAmount + 0.0001) {
      setError(
        `Total cannot exceed the due balance of ${formatInvoiceMoney(displayDueAmount, currency, {})}.`,
      );
    } else {
      setError("");
    }

    const cappedTotal = Math.min(total, displayDueAmount);
    const nextAmounts = splitPaymentTotalAcrossLineRows(
      displayLineRows,
      cappedTotal,
    );
    setLineAmounts((prev) => ({ ...prev, ...nextAmounts }));
  };

  const handleLineAmountChange = (lineNumber, value) => {
    const sanitized = sanitizeDecimalInput(value, PAYMENT_INPUT_DECIMALS);
    setLineAmounts((prev) => {
      const next = {
        ...prev,
        [lineNumber]: sanitized,
      };
      syncAllocationTotalInputFromLines(next);
      return next;
    });
    setError("");
  };

  const handleAllocationTotalChange = (value) => {
    const sanitized = sanitizeDecimalInput(value, PAYMENT_INPUT_DECIMALS);
    setAllocationTotalInput(sanitized);
    applyLiveAllocationSplit(sanitized);
  };

  const handleAllocationTotalBlur = () => {
    const normalized = normalizeInvoiceAmountInput(allocationTotalInput);
    setAllocationTotalInput(normalized);
    applyLiveAllocationSplit(normalized);
  };

  const handleLineAmountBlur = (lineNumber) => {
    setLineAmounts((prev) => {
      const next = {
        ...prev,
        [lineNumber]: normalizeInvoiceAmountInput(prev[lineNumber]),
      };
      syncAllocationTotalInputFromLines(next);
      return next;
    });
  };

  const handleReceiptChange = (fileList, { append = false } = {}) => {
    const incoming = Array.from(fileList || [])
      .filter(Boolean)
      .filter(isAllowedReceiptFile);

    if (!incoming.length) return;

    const nextFiles = append ? [...receiptFiles, ...incoming] : incoming;

    receiptPreviews.forEach((preview) => {
      if (preview) URL.revokeObjectURL(preview);
    });

    setReceiptFiles(nextFiles);
    setReceiptPreviews(nextFiles.map((file) => buildReceiptPreviewUrl(file)));
  };

  const handleReceiptRemove = (index) => {
    setReceiptFiles((prev) => prev.filter((_, i) => i !== index));
    setReceiptPreviews((prev) => {
      const preview = prev[index];
      if (preview) URL.revokeObjectURL(preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleClose = () => {
    if (isBusy) return;
    onClose?.();
  };

  const buildSharedPaymentFields = () => ({
    transaction_date: form.transaction_date,
    payment_method: form.payment_method,
    reference: String(form.reference || "").trim(),
    description: String(form.description || "").trim(),
    transaction_type: "payment",
  });

  const handleSave = async () => {
    if (!form.transaction_date) {
      setError("Payment date is required.");
      return;
    }

    if (useLineAllocation) {
      const selected = displayLineRows
        .map((row) => ({
          row,
          amount: Number(lineAmounts[row.line_number]) || 0,
        }))
        .filter((entry) => entry.amount > 0);

      if (!selected.length) {
        setError("Enter an amount for at least one line item.");
        return;
      }

      for (const { row, amount } of selected) {
        if (amount > row.remaining + 0.0001) {
          setError(
            `Amount for "${row.description}" cannot exceed ${formatInvoiceMoney(row.remaining, currency, {})}.`,
          );
          return;
        }
      }

      if (allocatedTotal > displayDueAmount + 0.0001) {
        setError(
          `Total payment cannot exceed the due balance of ${formatInvoiceMoney(displayDueAmount, currency, {})}.`,
        );
        return;
      }

      const shared = buildSharedPaymentFields();
      const entries = selected.map(({ row, amount }, index) => ({
        payment: {
          ...shared,
          amount,
          line_number: row.line_number,
          finance_invoice_item_id: row.finance_invoice_item_id,
          commission_installment_id: row.commission_installment_id,
          bonus_installment_id: row.bonus_installment_id,
          description:
            shared.description ||
            (row.description ? `Payment for ${row.description}` : "Payment received"),
        },
        receiptFiles: index === 0 ? receiptFiles : [],
      }));

      const saved = onSaveBulk
        ? await onSaveBulk(entries)
        : await onSave?.(
            entries[0].payment,
            receiptFiles[0] || null,
            receiptFiles,
          );

      if (saved) {
        onClose?.();
      }
      return;
    }

    const amount = Number(form.amount);
    if (!amount || amount <= 0) {
      setError("Enter a payment amount greater than zero.");
      return;
    }
    if (amount > displayDueAmount + 0.0001) {
      setError(
        `Amount cannot exceed the due balance of ${formatInvoiceMoney(displayDueAmount, currency, {})}.`,
      );
      return;
    }

    const saved = await onSave?.(
      {
        amount,
        ...buildSharedPaymentFields(),
      },
      receiptFiles[0] || null,
      receiptFiles,
    );

    if (saved) {
      onClose?.();
    }
  };

  if (!show) return null;

  const modalContent = (
    <>
      <div
        className="invoice-finance-payment-modal__backdrop"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        className={`invoice-finance-payment-modal${
          useWideLayout ? " invoice-finance-payment-modal--with-lines" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-finance-payment-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="invoice-finance-payment-modal__header">
          <div>
            <h4
              id="invoice-finance-payment-modal-title"
              className="invoice-finance-payment-modal__title mb-0"
            >
              Add payment
            </h4>
            {invoiceNumber ? (
              <p className="invoice-finance-payment-modal__subtitle mb-0">
                Invoice {invoiceNumber}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="invoice-finance-payment-modal__close"
            onClick={handleClose}
            disabled={isBusy}
            aria-label="Close"
          >
            <FaTimes size={14} />
          </button>
        </div>

        <div className="invoice-finance-payment-modal__body">
          {loading ? (
            <div className="invoice-finance-payment-modal__loading">
              <p className="mb-0 text-muted">Loading invoice…</p>
            </div>
          ) : loadError ? (
            <div className="invoice-finance-payment-modal__error mb-0">
              {loadError}
            </div>
          ) : (
            <>
          <div
            className={`invoice-finance-payment-modal__summaries${
              useLineAllocation
                ? " invoice-finance-payment-modal__summaries--split"
                : ""
            }`}
          >
            <div className="invoice-finance-payment-modal__summary">
              <span>Amount due</span>
              <strong>{formatInvoiceMoney(displayDueAmount, currency, {})}</strong>
            </div>
            {useLineAllocation ? (
              <div className="invoice-finance-payment-modal__summary invoice-finance-payment-modal__summary--accent">
                <span>Payment total</span>
                <strong>
                  {formatInvoiceMoney(allocatedTotal, currency, {})}
                </strong>
              </div>
            ) : null}
          </div>

          <div
            className={`invoice-finance-payment-modal__content${
              useLineAllocation
                ? " invoice-finance-payment-modal__content--split"
                : ""
            }`}
          >
            {useLineAllocation ? (
              <div className="invoice-finance-payment-modal__lines-panel">
                <div className="invoice-finance-payment-modal__field invoice-finance-payment-modal__allocation-total">
                  <label htmlFor="finance-invoice-payment-allocation-total">
                    Total payment amount
                  </label>
                  <input
                    id="finance-invoice-payment-allocation-total"
                    type="text"
                    inputMode="decimal"
                    className="form-control"
                    value={allocationTotalInput}
                    onChange={(event) =>
                      handleAllocationTotalChange(event.target.value)
                    }
                    onBlur={handleAllocationTotalBlur}
                    placeholder={`Max ${formatInvoiceMoney(displayDueAmount, currency, {})}`}
                    disabled={isBusy || displayDueAmount <= 0}
                  />
                  <p className="invoice-finance-payment-modal__hint mb-0">
                    Splits across lines by each line&apos;s due amount. You can
                    change any line amount after.
                  </p>
                </div>
                <div className="invoice-finance-payment-modal__lines-header">
                  <span>Line item</span>
                  <span>Due</span>
                  <span>Amount</span>
                </div>
                <div className="invoice-finance-payment-modal__line-items">
                  {displayLineRows.map((row) => (
                    <div
                      key={row.line_number}
                      className="invoice-finance-payment-modal__line-item"
                    >
                      <div className="invoice-finance-payment-modal__line-item-meta">
                        <strong title={row.description}>{row.description}</strong>
                      </div>
                      <span className="invoice-finance-payment-modal__line-item-due">
                        {formatInvoiceMoney(row.remaining, currency, {})}
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control"
                        value={lineAmounts[row.line_number] ?? ""}
                        onChange={(event) =>
                          handleLineAmountChange(
                            row.line_number,
                            event.target.value,
                          )
                        }
                        onBlur={() => handleLineAmountBlur(row.line_number)}
                        placeholder="0.00"
                        disabled={isBusy}
                        aria-label={`Amount for ${row.description}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="invoice-finance-payment-modal__details-panel">
              <div className="invoice-finance-payment-modal__grid">
                {!useLineAllocation ? (
                  <div className="invoice-finance-payment-modal__field">
                    <label htmlFor="finance-invoice-payment-amount">
                      Amount <span className="text-danger">*</span>
                    </label>
                    <input
                      id="finance-invoice-payment-amount"
                      type="text"
                      inputMode="decimal"
                      className="form-control"
                      value={form.amount}
                      onChange={(event) =>
                        handleFieldChange("amount", event.target.value)
                      }
                      onBlur={handleAmountBlur}
                      placeholder={`Max ${formatInvoiceMoney(displayDueAmount, currency, {})}`}
                      disabled={isBusy || displayDueAmount <= 0}
                    />
                  </div>
                ) : null}
            <div className="invoice-finance-payment-modal__field">
              <label htmlFor="finance-invoice-payment-date">
                Payment date <span className="text-danger">*</span>
              </label>
              <input
                id="finance-invoice-payment-date"
                type="date"
                className="form-control"
                value={form.transaction_date}
                max={toDateInputValue()}
                onChange={(event) =>
                  handleFieldChange("transaction_date", event.target.value)
                }
                disabled={isBusy}
              />
            </div>

            <div className="invoice-finance-payment-modal__field">
              <label htmlFor="finance-invoice-payment-method">
                Payment method <span className="text-danger">*</span>
              </label>
              <select
                id="finance-invoice-payment-method"
                className="form-select"
                value={form.payment_method}
                onChange={(event) =>
                  handleFieldChange("payment_method", event.target.value)
                }
                disabled={isBusy}
              >
                {PAYMENT_METHOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="invoice-finance-payment-modal__field">
              <label htmlFor="finance-invoice-payment-reference">
                Reference
              </label>
              <input
                id="finance-invoice-payment-reference"
                type="text"
                className="form-control"
                value={form.reference}
                onChange={(event) =>
                  handleFieldChange("reference", event.target.value)
                }
                placeholder="e.g. bank transfer ref"
                disabled={isBusy}
              />
            </div>
              </div>

              <div className="invoice-finance-payment-modal__field invoice-finance-payment-modal__field--description">
                <label htmlFor="finance-invoice-payment-description">
                  Description
                </label>
                <textarea
                  id="finance-invoice-payment-description"
                  className="form-control"
                  rows={useLineAllocation ? 2 : 3}
                  value={form.description}
                  onChange={(event) =>
                    handleFieldChange("description", event.target.value)
                  }
                  placeholder="Payment received"
                  disabled={isBusy}
                />
              </div>

              <div className="invoice-finance-payment-modal__field invoice-finance-payment-modal__field--receipts">
                <label htmlFor={receiptInputId}>
                  Receipts (multiple allowed)
                </label>
            {!receiptFiles.length ? (
              <div
                className="invoice-finance-payment-modal__dropzone"
                onClick={() =>
                  document.getElementById(receiptInputId)?.click()
                }
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  handleReceiptChange(event.dataTransfer.files);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    document.getElementById(receiptInputId)?.click();
                  }
                }}
              >
                <p className="mb-0 text-muted">
                  Drag & drop or click to upload one or more receipts
                </p>
                <input
                  id={receiptInputId}
                  type="file"
                  accept={RECEIPT_FILE_ACCEPT}
                  multiple
                  className="d-none"
                  onChange={(event) => {
                    handleReceiptChange(event.target.files);
                    event.target.value = "";
                  }}
                  disabled={isBusy}
                />
              </div>
            ) : (
              <div className="invoice-finance-payment-modal__files">
                {receiptFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="invoice-finance-payment-modal__file"
                  >
                    {isReceiptImageFile(file) && receiptPreviews[index] ? (
                      <img
                        src={receiptPreviews[index]}
                        alt={file.name}
                        className="invoice-finance-payment-modal__file-thumb"
                      />
                    ) : isReceiptPdfFile(file) ? (
                      <div className="invoice-finance-payment-modal__file-thumb invoice-finance-payment-modal__file-thumb--pdf">
                        PDF
                      </div>
                    ) : null}
                    <div className="invoice-finance-payment-modal__file-meta">
                      <p className="mb-0">{file.name}</p>
                      <p className="text-muted mb-0 small">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    {receiptPreviews[index] ? (
                      <button
                        type="button"
                        className="invoice-finance-payment-modal__file-preview"
                        onClick={() =>
                          window.open(receiptPreviews[index], "_blank")
                        }
                        title={`View ${file.name}`}
                        aria-label={`View ${file.name}`}
                        disabled={isBusy}
                      >
                        <FaEye />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="invoice-finance-payment-modal__file-remove"
                      onClick={() => handleReceiptRemove(index)}
                      disabled={isBusy}
                      aria-label="Remove receipt"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
                <label className="invoice-finance-payment-modal__add-more">
                  Add more files
                  <input
                    type="file"
                    accept={RECEIPT_FILE_ACCEPT}
                    multiple
                    className="d-none"
                    disabled={isBusy}
                    onChange={(event) => {
                      handleReceiptChange(event.target.files, { append: true });
                      event.target.value = "";
                    }}
                  />
                </label>
              </div>
            )}
              </div>
            </div>
          </div>

          {error ? (
            <div className="invoice-finance-payment-modal__error">{error}</div>
          ) : null}
            </>
          )}
        </div>

        <div className="invoice-finance-payment-modal__footer">
          <button
            type="button"
            className="invoice-finance-payment-modal__btn invoice-finance-payment-modal__btn--cancel"
            onClick={handleClose}
            disabled={isBusy}
          >
            {loadError ? "Close" : "Cancel"}
          </button>
          {!loadError ? (
            <button
              type="button"
              className="invoice-finance-payment-modal__btn invoice-finance-payment-modal__btn--save"
              onClick={handleSave}
              disabled={!canSave}
            >
              {saving ? "Saving…" : "Save payment"}
            </button>
          ) : null}
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}

export default FinanceInvoicePaymentModal;
